/**
 * @module ui.slider
 * @version 4.7.0
 * @description LIFTR Slider Web Component - Carousel with touch, autoplay, and accessibility
 * 
 * @element liftr-slider
 * 
 * @fires liftr:slider:changed - When slide changes
 * @fires liftr:slider:enabled - When slider becomes enabled
 * @fires liftr:slider:disabled - When slider becomes disabled
 * 
 * @attr {string} liftr-id - Required for static mode (target reference)
 * @attr {string} liftr-axis - Always "x" (horizontal)
 * @attr {string} liftr-state - State output: disabled, dragging, can-scroll
 * @attr {string} liftr-nav - Navigation output (AUTO MODE ONLY)
 * 
 * @changelog
 * - 4.7.0: PERF: Clone-chain read/write phase separation
 *        - #initialSetup(): 5-phase structure (non-geometric DOM, pre-clone READ,
 *          clone mutations, post-clone READ, writes from cache, autoplay)
 *        - #enableSlider(): Same 4-phase structure
 *        - #handleChanges() rebuildClones: Explicit read/write phases per path
 *        - Eliminates read-between-writes in mount/enable/resize cycles
 *        - Netto: 2 reflow cycles (pre-clone + post-clone) + 1 intentional flush
 * - 4.6.0: PERF: Layout geometry cache
 *        - New: #layoutCache, #readLayout(), #invalidateLayout()
 *        - #getVisibleCount(), #getSlideStep(), #getItemWidth() read from cache
 *        - Eliminates 5-8 synchronous reflows per mount/resize cycle
 *        - #updateScrollCapability() hybrid: containerWidth cached, scrollWidth live
 *        - #createClones() reads cache before DOM mutations, invalidates after
 *        - #setTransform() non-loop uses cached step/itemWidth/containerWidth
 *        - Invalidation: enable, disable, createClones, removeClones, handleChanges(size)
 * - 4.5.1: FIX: Replace inert with aria-hidden + tabindex
 *        - Root cause: inert on slider-items breaks touch-scroll on CriOS/WebKit
 *          after dialog.close() — browser fails to clean up inert on ~20 elements
 *        - Replace: inert attribute on original slides and clones
 *        - With: aria-hidden="true" (already present) + tabindex="-1" on focusable children
 *        - CSS pointer-events: none on [aria-hidden="true"] already handles click/touch
 *        - New: FOCUSABLE_SELECTOR constant, #setChildrenFocusable() helper
 *        - Cleanup: unmount() and #disableSlider() restore tabindex on children
 * - 4.5.0: CONFIG BOUNDARY
 *        - REMOVE: #getSetting() bridge method (anti-pattern, see liftr-core Section 5)
 *        - REMOVE: Overlap keys from DEFAULTS (enabled, loop, mode, autoplay, dots)
 *        - ADD: #css() accessor for CSS_SCHEMA values
 *        - FIX: CSS_SCHEMA.loop.default null -> true (was bridge workaround)
 *        - REMOVE: Redundant ?? DEFAULTS fallbacks on this.config reads
 * - 4.4.0: FEATURE: Progress Bar indicator (Pattern A)
 *        - New dots mode: 'bar' (thin track + proportional thumb)
 *        - Thumb width = visibleCount / totalSlides (min 8%)
 *        - Thumb position via --slider-progress CSS custom property
 *        - Progress update fires BEFORE transform (synced animation)
 *        - Live thumb tracking during drag (fractional index from offset)
 *        - Pure visual feedback, no click/drag interaction
 *        - aria-hidden="true" — decorative only
 *        - New ROLES: PROGRESS, THUMB
 *        - New PROPERTIES: PROGRESS
 *        - CSS_SCHEMA.dots accepts: 'false' | 'true' | 'bar'
 * - 4.3.6: FIX: Clone aria-hidden causing pointer-events: none
 *        - Reverted 4.3.5 updateSlides-before-transition (caused visual bugs)
 *        - Root cause: CSS rule [aria-hidden="true"] { pointer-events: none }
 *        - Clones had permanent aria-hidden="true", never updated after navigation
 *        - Fix: #updateSlides() now manages clone aria-hidden based on viewport position
 *        - Visible clones get aria-hidden="false" + inert removed
 *        - Hidden clones get aria-hidden="true" + inert set
 *        - Peek items: interactive range extends 1 item on BOTH sides
 *          (left + right) so partially visible items at either viewport
 *          edge are never inert
 * - 4.3.4: FIX: Click suppression architecture
 *        - Replaced once:true listener with permanent flag-based suppression
 *        - #suppressNextClick flag: set on drag-end, cleared on next click OR pointerdown
 *        - Eliminates timing-dependent setTimeout workaround
 *        - Eliminates ghost listener from setPointerCapture consuming clicks
 *        - Fixes "Add to Cart" not clickable after drag on product cards
 * - 4.3.3: DRAG ON INTERACTIVE ELEMENTS
 *        - Removed interactive element guard from pointerdown
 *        - Two-phase drag: track first, commit after 10px threshold
 *        - Click suppression on interactive elements after drag
 *        - Native dragstart prevention during tracking
 *        - Proportional slow-drag: slides moved = distance / step
 * - 4.3.2: AUDIT COMPLIANCE - Quality Checks v4.8
 *        - CSS_SCHEMA -> Object.freeze() for consistency
 * - 4.3.1: FX.PROPERTIES integration
 *        - Timing from FX.PROPERTIES.MORPH_DURATION (global FX Set)
 *        - Removed slider-specific DURATION/EASING from PROPERTIES
 * - 4.3.0: AUDIT COMPLIANCE - Quality Checks v4.4
 *        - ROLES/STATES/EVENTS/SETTINGS/PROPERTIES constants
 *        - All string literals -> constants
 *        - FX Set integration (CSS-driven timing)
 *        - Correct export pattern
 * - 4.2.0: NAV refactor - clear auto/static separation
 * - 4.1.0: Using NAV.* helpers from core.liftr.js
 * - 4.0.0: Initial v4.0 implementation
 * 
 * Features:
 * - Responsive layout modes (auto/fixed with optional peek) via CSS only
 * - Infinite loop (dynamic clone-based, adapts to visible slide count)
 * - Touch/swipe navigation (Pointer Events)
 * - Prev/Next arrow navigation (auto-generated or static via liftr-target)
 * - Dot pagination (auto-generated or existing)
 * - Progress bar indicator (proportional thumb on thin track)
 * - Autoplay with pause control
 * - Declarative edge mask via liftr-mask attribute
 * - Responsive enable/disable via CSS Settings
 * - Full ARIA compliance with live region
 * - Reduced motion support
 * - Auto-disable navigation when content fits (can-scroll state)
 * - Navigation modes: single (1 slide) or page (all visible)
 * - FX Set dependent timing (refined/soft/quiet)
 * 
 * @example
 * <!-- AUTO MODE: Buttons generated, liftr-nav on root -->
 * <liftr-slider liftr-id="hero-slider">
 *   <div liftr-role="slider-viewport" liftr-mask="left right">
 *     <div liftr-role="slider-panel" liftr-axis="x">
 *       <div liftr-role="slider-item">Slide 1</div>
 *     </div>
 *   </div>
 * </liftr-slider>
 * 
 * @example
 * <!-- STATIC MODE: External buttons with liftr-target -->
 * <button liftr-target="hero-slider" liftr-role="slider-prev" liftr-nav="medium arrow light round">Prev</button>
 * <button liftr-target="hero-slider" liftr-role="slider-next" liftr-nav="medium arrow light round">Next</button>
 * 
 * <liftr-slider liftr-id="hero-slider">
 *   <!-- No liftr-nav on root, buttons are external -->
 * </liftr-slider>
 * 
 * @example
 * <!-- PROGRESS BAR: CSS setting dots: bar -->
 * <!-- Section CSS: .hero_slider { --slider-dots: bar; } -->
 * <liftr-slider class="hero_slider">
 *   <div liftr-role="slider-viewport">
 *     <div liftr-role="slider-panel" liftr-axis="x">
 *       <div liftr-role="slider-item">Slide 1</div>
 *     </div>
 *   </div>
 * </liftr-slider>
 */

import { LiftrModule } from 'core.module';
import { ATTRIBUTES, i18n, NAV, FX } from 'core.liftr';
import { 
  setCSSVar, 
  prefersReducedMotion,
  on
} from 'core.utilities';

/* =====================================================================
 * CONSTANTS
 * ===================================================================== */

const ROLES = Object.freeze({
  VIEWPORT: 'slider-viewport',
  PANEL:    'slider-panel',
  ITEM:     'slider-item',
  PREV:     'slider-prev',
  NEXT:     'slider-next',
  DOT:      'slider-dot',
  PAUSE:    'slider-pause',
  NAV:      'slider-nav',
  LIVE:     'slider-live',
  PROGRESS: 'slider-progress',
  THUMB:    'slider-thumb'
});

const STATES = Object.freeze({
  DISABLED:   'disabled',
  DRAGGING:   'dragging',
  CAN_SCROLL: 'can-scroll'
});

const EVENTS = Object.freeze({
  CHANGED:  'changed',
  ENABLED:  'enabled',
  DISABLED: 'disabled'
});

const SETTINGS = Object.freeze({});

const PROPERTIES = Object.freeze({
  OFFSET:      '--slider-offset',
  DRAG:        '--slider-drag',
  COLS:        '--slider-cols',
  PROGRESS:    '--slider-progress',
  THUMB_WIDTH: '--slider-thumb-width'
});

const CSS_SCHEMA = Object.freeze({
  loop:     { type: 'bool',   default: true },
  autoplay: { type: 'bool',   default: false },
  dots:     { type: 'string', default: 'false' },
  mode:     { type: 'string', default: 'single' }
});

/**
 * Selector for focusable elements inside slides.
 * Used to toggle tabindex on hidden slides (Splide pattern).
 * Why not inert: breaks touch-scroll on CriOS/WebKit after dialog close.
 */
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), iframe';

/* =====================================================================
 * LIFTR SLIDER WEB COMPONENT
 * ===================================================================== */

class LiftrSlider extends LiftrModule {
  
  static NAME = 'slider';
  
  static DEFAULTS = {
    initialSlide: 0,
    autoplayInterval: 5000,
    pauseOnHover: true,
    pauseOnFocus: true,
    swipeThreshold: 50,
    swipeTimeout: 300
  };

  /* --- Private Fields --- */
  
  #viewport = null;
  #panel = null;
  #originalSlides = [];
  #allSlides = [];
  #prevBtn = null;
  #nextBtn = null;
  #dotsContainer = null;
  #dots = [];
  #pauseBtn = null;
  #liveRegion = null;
  
  #generatedPrevBtn = null;
  #generatedNextBtn = null;
  #generatedDotsContainer = null;
  #generatedLiveRegion = null;
  #generatedProgressBar = null;
  
  #progressBar = null;
  #progressThumb = null;
  
  #clonesStart = [];
  #clonesEnd = [];
  #cloneCount = 0;
  
  #currentIndex = 0;
  #realIndex = 0;
  #totalSlides = 0;
  #isDisabled = false;
  #isDragging = false;
  #isAutoplayPaused = false;
  #isTransitioning = false;
  #autoplayTimer = null;
  #reducedMotion = false;
  
  #dragStartX = 0;
  #dragStartY = 0;
  #dragStartTime = 0;
  #dragOffset = 0;
  #dragStep = 0;
  #dragProgressCache = null;
  #isTracking = false;
  #isOnInteractive = false;
  #suppressNextClick = false;
  
  #cssSettings = null;
  #navSettings = null;
  #viewportMask = null;
  #labels = null;
  
  #layoutCache = null;
  
  #navCleanups = [];
  #dotCleanups = [];
  #autoplayCleanups = [];
  
  /* --- Lifecycle --- */
  
  mount() {
    this.#labels = i18n.get('slider') || {
      prev: 'Previous slide',
      next: 'Next slide',
      pause: 'Pause slider',
      play: 'Play slider',
      slideOf: 'Slide {current} of {total}',
      goToSlide: 'Go to slide {index}'
    };
    
    this.#viewport = this.role(ROLES.VIEWPORT);
    this.#panel = this.role(ROLES.PANEL);
    this.#originalSlides = [...this.roleAll(ROLES.ITEM)];
    
    if (!this.#viewport || !this.#panel || this.#originalSlides.length === 0) {
      throw new Error('[slider] Viewport, panel or slides not found');
    }
    
    this.#totalSlides = this.#originalSlides.length;
    this.#realIndex = this.config.initialSlide ?? 0;
    this.#reducedMotion = prefersReducedMotion();
    this.#viewportMask = this.createMask(this.#viewport);
    
    this.#dotsContainer = this.role(ROLES.NAV);
    this.#pauseBtn = this.role(ROLES.PAUSE);
    this.#liveRegion = this.role(ROLES.LIVE);
    
    this.#setupARIA();
    
    const watcher = this.watchSettings('slider', {
      enabled: '--slider-enabled',
      css: CSS_SCHEMA,
      nav: true,
      resize: true,
      debounce: 100
    }, {
      onEnable: (s) => this.#enableSlider(s),
      onDisable: () => this.#disableSlider(),
      onChange: (changes) => this.#handleChanges(changes)
    });
    
    this.#isDisabled = !watcher.isEnabled();
    this.#setupPermanentListeners();
    
    if (!this.#isDisabled) {
      this.#initialSetup();
    }
  }
  
  unmount() {
    this.#isTracking = false;
    this.#isOnInteractive = false;
    this.#suppressNextClick = false;
    this.#stopAutoplay();
    this.#cleanupNav();
    this.#cleanupDots();
    this.#cleanupAutoplay();
    this.#removeClones();
    this.#removeNavigation();
    this.#removeDots();
    this.#removeProgressBar();
    this.#removeLiveRegion();
    
    setCSSVar(this.#panel, PROPERTIES.OFFSET, '');
    setCSSVar(this.#panel, PROPERTIES.DRAG, '');
    
    this.#originalSlides.forEach(slide => {
      slide.removeAttribute('aria-hidden');
      slide.removeAttribute('liftr-active');
      this.#setChildrenFocusable(slide, true);
    });
    
    super.unmount();
  }
  
  /* --- ARIA --- */
  
  #setupARIA() {
    this.setAttribute('aria-roledescription', 'slider');
    if (!this.getAttribute('aria-label')) {
      this.setAttribute('aria-label', 'Content slider');
    }
    
    this.#originalSlides.forEach(slide => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
    });
    
    if (this.#pauseBtn && this.#css('autoplay') && !this.#reducedMotion) {
      this.#pauseBtn.setAttribute('aria-label', this.#labels.pause);
      this.#pauseBtn.setAttribute('aria-pressed', 'false');
    }
    
    if (this.#pauseBtn && (!this.#css('autoplay') || this.#reducedMotion)) {
      this.#pauseBtn.hidden = true;
    }
  }
  
  /* --- Permanent Listeners --- */
  
  #setupPermanentListeners() {
    this.addDisposable(on(this, 'pointerdown', (e) => this.#handlePointerDown(e)));
    this.addDisposable(on(this, 'pointermove', (e) => this.#handlePointerMove(e)));
    this.addDisposable(on(this, 'pointerup', (e) => this.#handlePointerUp(e)));
    this.addDisposable(on(this, 'pointercancel', (e) => this.#handlePointerUp(e)));
    
    this.addDisposable(on(this, 'dragstart', (e) => {
      if (this.#isTracking) e.preventDefault();
    }));
    
    this.addDisposable(on(this.#panel, 'touchmove', (e) => {
      if (this.#isDragging && Math.abs(this.#dragOffset) > 10) {
        e.preventDefault();
      }
    }, { passive: false }));
    
    this.addDisposable(on(this, 'keydown', (e) => this.#handleKeydown(e)));
    
    // Permanent click suppression (flag-controlled, v4.3.4)
    this.addDisposable(on(this, 'click', (e) => {
      if (this.#suppressNextClick) {
        this.#suppressNextClick = false;
        e.preventDefault();
        e.stopPropagation();
      }
    }, { capture: true }));
  }
  
  /* --- Initial Setup --- */
  
  #initialSetup() {
    // Phase 0: Non-geometric DOM setup
    this.#createLiveRegion();
    this.#createNavigation();
    this.#createDots();
    this.#createProgressBar();
    
    if (this.#dotsContainer && !this.#generatedDotsContainer) {
      this.#dots = [...this.#dotsContainer.querySelectorAll(`[${ATTRIBUTES.ROLE}~="${ROLES.DOT}"]`)];
      if (this.#dots.length === 0) {
        this.#dots = [...this.#dotsContainer.querySelectorAll('button, [role="tab"]')];
      }
    }
    
    this.#setupNavListeners();
    this.#setupDotListeners();
    this.#setupAutoplayListeners();
    
    // Phase 1: PRE-CLONE READ
    this.#invalidateLayout();
    this.#readLayout();
    
    // Phase 2: CLONE DOM MUTATIONS
    this.#createClones();
    if (!this.#css('loop')) {
      this.#allSlides = [...this.#originalSlides];
      this.#currentIndex = this.#realIndex;
    }
    
    // Phase 3: POST-CLONE READ
    this.#readLayout();
    
    // Phase 4: WRITES (all from cache)
    this.#setTransform(this.#currentIndex, false);
    this.#showClones();
    this.#updateSlides();
    this.#updateMask();
    this.#updateScrollCapability();
    
    // Phase 5: Autoplay
    if (this.#css('autoplay') && !this.#reducedMotion) {
      this.#startAutoplay();
    }
  }
  
  /* --- Helpers --- */
  
  /**
   * Read CSS-driven setting. Single source: CSS_SCHEMA via watchSettings.
   * @param {string} key - CSS_SCHEMA key (loop, autoplay, dots, mode)
   * @returns {*} Current value or CSS_SCHEMA default
   */
  #css(key) {
    return this.#cssSettings?.[key] ?? CSS_SCHEMA[key]?.default;
  }
  
  /**
   * Toggle focusability of interactive children inside a slide.
   * Sets tabindex="-1" on hidden slides, removes it on visible ones.
   * Combined with aria-hidden + CSS pointer-events: none, this replaces inert.
   * @param {Element} slide - Slide element
   * @param {boolean} focusable - Whether children should be focusable
   */
  #setChildrenFocusable(slide, focusable) {
    const children = slide.querySelectorAll(FOCUSABLE_SELECTOR);
    if (focusable) {
      for (const el of children) el.removeAttribute('tabindex');
    } else {
      for (const el of children) el.setAttribute('tabindex', '-1');
    }
  }

  #canNavigate() {
    return !this.#isDisabled && !this.#isTransitioning && this.state.has(STATES.CAN_SCROLL);
  }
  
  #formatLabel(template, vars) {
    return template.replace(/{(\w+)}/g, (_, key) => vars[key] ?? '');
  }
  
  #getTransitionDuration() {
    const raw = getComputedStyle(this.#panel).transitionDuration;
    if (!raw || raw === '0s') return 300;
    const value = parseFloat(raw);
    if (raw.endsWith('ms')) return value;
    if (raw.endsWith('s')) return value * 1000;
    return 300;
  }
  
  /* --- Layout Geometry Cache (v4.6.0) --- */
  
  /**
   * Read all layout geometry values once, cache for the current cycle.
   * Subsequent calls return the cached object until #invalidateLayout().
   * Eliminates repeated synchronous reflows from offsetWidth/offsetLeft reads.
   * @returns {{ containerWidth: number, itemWidth: number, step: number, visibleCount: number }}
   */
  #readLayout() {
    if (this.#layoutCache) return this.#layoutCache;

    // 1. Visible count from CSS setting
    const colsValue = getComputedStyle(this).getPropertyValue(PROPERTIES.COLS).trim();

    // 2. Container width
    const containerWidth = this.#viewport.offsetWidth;

    // 3. Item width (reference slide -- first original, offset by clones if loop)
    const targetSlide = this.#css('loop')
      ? this.#allSlides[this.#cloneCount]
      : this.#originalSlides[0];
    const itemWidth = targetSlide?.offsetWidth || 0;

    // 4. Slide step (distance between consecutive slides)
    const slides = this.#css('loop') ? this.#allSlides : this.#originalSlides;
    const baseIndex = this.#css('loop') ? this.#cloneCount : 0;
    let step = itemWidth;
    if (slides.length >= 2 && slides[baseIndex] && slides[baseIndex + 1]) {
      step = slides[baseIndex + 1].offsetLeft - slides[baseIndex].offsetLeft;
    }

    // 5. Visible count
    let visibleCount = 1;
    if (colsValue) {
      const cols = parseInt(colsValue, 10);
      if (!isNaN(cols) && cols > 0) visibleCount = cols;
    } else if (step > 0) {
      visibleCount = Math.max(1, Math.floor(containerWidth / step));
    }

    this.#layoutCache = Object.freeze({ containerWidth, itemWidth, step, visibleCount });
    return this.#layoutCache;
  }

  /**
   * Invalidate the layout cache. Must be called after any DOM mutation
   * that changes element geometry (clone insertion/removal, enable/disable).
   * NOT needed after navigation, drag, or autoplay (transform-only changes).
   */
  #invalidateLayout() {
    this.#layoutCache = null;
  }

  /* --- Visible Count & Page Helpers --- */
  
  #getVisibleCount() {
    return this.#readLayout().visibleCount;
  }
  
  #getPerMove() {
    if (this.#css('mode') !== 'all') return 1;
    return Math.min(this.#getVisibleCount(), this.#totalSlides);
  }
  
  #getPageStarts() {
    const perMove = this.#getPerMove();
    if (perMove <= 1) {
      return Array.from({ length: this.#totalSlides }, (_, i) => i);
    }
    const maxIndex = this.#getMaxIndex();
    const pages = [];
    for (let i = 0; i <= maxIndex; i += perMove) {
      pages.push(i);
    }
    if (pages.length === 0 || pages[pages.length - 1] < maxIndex) {
      pages.push(maxIndex);
    }
    return pages;
  }
  
  #getPageCount() {
    return this.#getPageStarts().length;
  }
  
  #getCurrentPage() {
    const pageStarts = this.#getPageStarts();
    for (let i = pageStarts.length - 1; i >= 0; i--) {
      if (this.#realIndex >= pageStarts[i]) return i;
    }
    return 0;
  }
  
  #getMaxIndex() {
    if (this.#css('loop')) return this.#totalSlides - 1;
    const visibleCount = this.#getVisibleCount();
    return Math.max(0, this.#totalSlides - visibleCount);
  }
  
  /* --- Mask --- */
  
  #updateMask() {
    if (!this.#viewportMask?.has()) return;
    if (this.#css('loop')) return;
    const maxIndex = this.#getMaxIndex();
    this.#viewportMask.toggle('left', this.#realIndex > 0);
    this.#viewportMask.toggle('right', this.#realIndex < maxIndex);
  }
  
  /* --- Scroll Capability --- */
  
  #updateScrollCapability() {
    if (this.#isDisabled) {
      this.state.remove(STATES.CAN_SCROLL);
      return;
    }
    const { containerWidth } = this.#readLayout();
    const contentWidth = this.#panel.scrollWidth;  // must stay live (changes with clones)
    const canScroll = contentWidth > containerWidth + 1;
    if (canScroll) {
      this.state.add(STATES.CAN_SCROLL);
    } else {
      this.state.remove(STATES.CAN_SCROLL);
    }
  }
  
  /* --- Navigation UI --- */
  
  #createNavigation() {
    if (!this.#navSettings || this.#navSettings.mode === 'false') {
      this.removeAttribute(ATTRIBUTES.NAV);
      return;
    }
    if (this.#navSettings.mode === 'auto') {
      NAV.applyOutput(this, this.#navSettings);
      this.#prevBtn = NAV.createButton(ROLES.PREV, 'prev', this.#labels.prev);
      this.#nextBtn = NAV.createButton(ROLES.NEXT, 'next', this.#labels.next);
      this.appendChild(this.#prevBtn);
      this.appendChild(this.#nextBtn);
      this.#generatedPrevBtn = this.#prevBtn;
      this.#generatedNextBtn = this.#nextBtn;
    } else if (this.#navSettings.mode === 'static') {
      this.removeAttribute(ATTRIBUTES.NAV);
      this.#prevBtn = this.target(ROLES.PREV);
      this.#nextBtn = this.target(ROLES.NEXT);
      if (this.#prevBtn) {
        NAV.applyToButton(this.#prevBtn, this.#navSettings);
        NAV.ensureRoles(this.#prevBtn, 'prev');
      }
      if (this.#nextBtn) {
        NAV.applyToButton(this.#nextBtn, this.#navSettings);
        NAV.ensureRoles(this.#nextBtn, 'next');
      }
    }
  }
  
  #removeNavigation() {
    this.#generatedPrevBtn?.remove();
    this.#generatedNextBtn?.remove();
    this.#generatedPrevBtn = null;
    this.#generatedNextBtn = null;
    this.#prevBtn = null;
    this.#nextBtn = null;
    this.removeAttribute(ATTRIBUTES.NAV);
  }
  
  /* --- Dots --- */
  
  #createDots() {
    const dotsConfig = this.#css('dots');
    if (!dotsConfig || dotsConfig === 'false' || dotsConfig === 'bar') return;
    
    if (dotsConfig === true || dotsConfig === 'true' || dotsConfig === 'auto') {
      this.#dotsContainer = document.createElement('div');
      this.#dotsContainer.setAttribute('role', 'tablist');
      this.#dotsContainer.setAttribute('aria-label', 'Slide controls');
      this.#dotsContainer.setAttribute(ATTRIBUTES.ROLE, ROLES.NAV);
      
      const pageStarts = this.#getPageStarts();
      const currentPage = this.#getCurrentPage();
      
      pageStarts.forEach((startIndex, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-selected', String(i === currentPage));
        dot.setAttribute('aria-label', this.#formatLabel(this.#labels.goToSlide, { index: i + 1 }));
        dot.setAttribute(ATTRIBUTES.ROLE, ROLES.DOT);
        dot.dataset.page = i;
        dot.dataset.startIndex = startIndex;
        this.#dotsContainer.appendChild(dot);
      });
      
      this.appendChild(this.#dotsContainer);
      this.#generatedDotsContainer = this.#dotsContainer;
    }
    
    this.#dots = [...(this.#dotsContainer?.querySelectorAll(`[${ATTRIBUTES.ROLE}~="${ROLES.DOT}"]`) || [])];
    if (this.#dots.length === 0 && this.#dotsContainer) {
      this.#dots = [...this.#dotsContainer.querySelectorAll('button, [role="tab"]')];
    }
  }
  
  #removeDots() {
    if (this.#generatedDotsContainer) {
      this.#generatedDotsContainer.remove();
      this.#generatedDotsContainer = null;
      this.#dotsContainer = null;
      this.#dots = [];
    }
  }
  
  /* --- Progress Bar (v4.4.0) --- */
  
  /**
   * Create progress bar indicator (dots: 'bar').
   * Generates: track container + proportional thumb.
   * Pure visual — no interaction, aria-hidden.
   */
  #createProgressBar() {
    const dotsConfig = this.#css('dots');
    if (dotsConfig !== 'bar') return;
    
    this.#progressBar = document.createElement('div');
    this.#progressBar.setAttribute(ATTRIBUTES.ROLE, ROLES.PROGRESS);
    this.#progressBar.setAttribute('aria-hidden', 'true');
    
    this.#progressThumb = document.createElement('div');
    this.#progressThumb.setAttribute(ATTRIBUTES.ROLE, ROLES.THUMB);
    
    this.#progressBar.appendChild(this.#progressThumb);
    this.appendChild(this.#progressBar);
    this.#generatedProgressBar = this.#progressBar;
    
    this.#updateProgressBar();
  }
  
  /**
   * Remove generated progress bar.
   */
  #removeProgressBar() {
    if (this.#generatedProgressBar) {
      this.#generatedProgressBar.remove();
      this.#generatedProgressBar = null;
      this.#progressBar = null;
      this.#progressThumb = null;
    }
  }
  
  /**
   * Update progress bar thumb width and position.
   * 
   * thumbWidth = visibleCount / totalSlides (clamped min 8%)
   * progress   = effectiveIndex / maxIndex (0 when maxIndex is 0)
   * thumbLeft  = progress * (1 - thumbWidth)
   * 
   * @param {number} [effectiveIndex=this.#realIndex] - Current position.
   *   During drag: fractional index derived from drag offset.
   *   Otherwise: discrete #realIndex after navigation.
   * 
   * Width is set inline (changes with resize).
   * Position is set via --slider-progress custom property
   * so CSS handles the transition timing.
   */
  #updateProgressBar(effectiveIndex) {
    if (!this.#progressBar || !this.#progressThumb) return;
    
    const index = effectiveIndex ?? this.#realIndex;
    const visibleCount = this.#getVisibleCount();
    const thumbWidth = Math.max(0.08, visibleCount / this.#totalSlides);
    const maxIndex = this.#getMaxIndex();
    const clamped = Math.max(0, Math.min(index, maxIndex));
    const progress = maxIndex === 0 ? 0 : clamped / maxIndex;
    const thumbLeft = progress * (1 - thumbWidth);
    
    setCSSVar(this.#progressBar, PROPERTIES.THUMB_WIDTH, thumbWidth);
    setCSSVar(this.#progressBar, PROPERTIES.PROGRESS, thumbLeft);
  }
  
  /* --- Live Region --- */
  
  #createLiveRegion() {
    if (!this.#liveRegion) {
      this.#liveRegion = document.createElement('div');
      this.#liveRegion.setAttribute('aria-live', 'polite');
      this.#liveRegion.setAttribute('aria-atomic', 'true');
      this.#liveRegion.className = 'sr-only';
      this.#liveRegion.setAttribute(ATTRIBUTES.ROLE, ROLES.LIVE);
      this.appendChild(this.#liveRegion);
      this.#generatedLiveRegion = this.#liveRegion;
    }
  }
  
  #removeLiveRegion() {
    if (this.#generatedLiveRegion) {
      this.#generatedLiveRegion.remove();
      this.#generatedLiveRegion = null;
      this.#liveRegion = null;
    }
  }
  
  /* --- Clones (Infinite Loop) --- */
  
  #createClones() {
    if (!this.#css('loop') || this.#totalSlides < 2) return;
    
    // Read geometry from cache BEFORE DOM mutations
    const { containerWidth, itemWidth } = this.#readLayout();
    const visibleCount = Math.ceil(containerWidth / (itemWidth || 1));
    this.#cloneCount = Math.min(visibleCount, this.#totalSlides);
    
    for (let i = 0; i < this.#cloneCount; i++) {
      const sourceIndex = i % this.#totalSlides;
      const clone = this.#originalSlides[sourceIndex].cloneNode(true);
      clone.setAttribute('liftr-clone', 'end');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      clone.removeAttribute('liftr-active');
      clone.style.visibility = 'hidden';
      this.#panel.appendChild(clone);
      this.#setChildrenFocusable(clone, false);
      this.#clonesEnd.push(clone);
    }
    
    for (let i = this.#cloneCount - 1; i >= 0; i--) {
      const sourceIndex = this.#totalSlides - 1 - (i % this.#totalSlides);
      const clone = this.#originalSlides[sourceIndex].cloneNode(true);
      clone.setAttribute('liftr-clone', 'start');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      clone.removeAttribute('liftr-active');
      clone.style.visibility = 'hidden';
      this.#panel.insertBefore(clone, this.#panel.firstChild);
      this.#setChildrenFocusable(clone, false);
      this.#clonesStart.unshift(clone);
    }
    
    this.#allSlides = [...this.#clonesStart, ...this.#originalSlides, ...this.#clonesEnd];
    this.#currentIndex = this.#realIndex + this.#cloneCount;
    
    // Invalidate -- DOM has changed, cached offsets are stale
    this.#invalidateLayout();
  }
  
  #showClones() {
    [...this.#clonesStart, ...this.#clonesEnd].forEach(clone => {
      clone.style.removeProperty('visibility');
      if (!clone.style.length) clone.removeAttribute('style');
    });
  }
  
  #removeClones() {
    this.#clonesStart.forEach(clone => clone.remove());
    this.#clonesEnd.forEach(clone => clone.remove());
    this.#clonesStart = [];
    this.#clonesEnd = [];
    this.#cloneCount = 0;
    this.#allSlides = [...this.#originalSlides];
    this.#invalidateLayout();
  }
  
  /* --- Transform & Update --- */
  
  #getItemWidth() {
    return this.#readLayout().itemWidth;
  }
  
  #getSlideStep() {
    return this.#readLayout().step;
  }
  
  #setTransform(index, animate = true) {
    const { step, itemWidth, containerWidth } = this.#readLayout();
    let offset = index * step;
    
    if (!this.#css('loop')) {
      const gap = step - itemWidth;
      const maxOffset = this.#panel.scrollWidth - containerWidth + gap;
      offset = Math.min(offset, Math.max(0, maxOffset));
    }
    
    if (!animate || this.#reducedMotion) {
      this.#panel.style.transition = 'none';
      setCSSVar(this.#panel, PROPERTIES.OFFSET, `-${offset}px`);
      this.#panel.offsetHeight;
      this.#panel.style.transition = '';
    } else {
      setCSSVar(this.#panel, PROPERTIES.OFFSET, `-${offset}px`);
    }
  }
  
  /**
   * Update slide interactivity (inert, aria-hidden) and visual state.
   * 
   * v4.3.6: Also manages clone attributes. Clones in the visible window
   * get aria-hidden="false" to prevent CSS [aria-hidden="true"] { pointer-events: none }
   * from blocking interactions on visible clones at loop edges.
   */
  #updateSlides() {
    const visibleCount = this.#getVisibleCount();

    // Interactive range: extend 1 item on BOTH sides for peek items.
    // Items partially visible at either edge must be interactive.
    const interactiveStart = this.#realIndex - 1;
    const interactiveEnd = this.#realIndex + visibleCount + 1;

    this.#originalSlides.forEach((slide, i) => {
      let isVisible;
      if (!this.#css('loop')) {
        isVisible = i >= interactiveStart && i < interactiveEnd;
      } else {
        isVisible = (i >= interactiveStart && i < interactiveEnd)
          || (interactiveEnd > this.#totalSlides && i < interactiveEnd - this.#totalSlides)
          || (interactiveStart < 0 && i >= this.#totalSlides + interactiveStart);
      }

      const isActive = i === this.#realIndex;

      slide.setAttribute('aria-hidden', String(!isVisible));
      slide.setAttribute('aria-label', this.#formatLabel(this.#labels.slideOf, {
        current: i + 1,
        total: this.#totalSlides
      }));

      this.#setChildrenFocusable(slide, isVisible);

      if (isActive) {
        slide.setAttribute('liftr-active', '');
      } else {
        slide.removeAttribute('liftr-active');
      }
    });

    // v4.3.6: Update clone interactivity based on viewport position.
    if (this.#css('loop') && this.#cloneCount > 0) {
      const visibleStart = this.#currentIndex - 1;
      const visibleEnd = this.#currentIndex + visibleCount + 1;

      this.#allSlides.forEach((slide, i) => {
        if (!slide.hasAttribute('liftr-clone')) return;
        const isInWindow = i >= visibleStart && i < visibleEnd;
        slide.setAttribute('aria-hidden', String(!isInWindow));
        this.#setChildrenFocusable(slide, isInWindow);
      });
    }

    this.#updateDotsUI();
    this.#updateNavButtons();
    this.#announceSlide();
  }
  
  #updateDotsUI() {
    const currentPage = this.#getCurrentPage();
    this.#dots.forEach((dot, i) => {
      dot.setAttribute('aria-selected', String(i === currentPage));
    });
  }
  
  #updateNavButtons() {
    if (!this.#css('loop')) {
      const maxIndex = this.#getMaxIndex();
      if (this.#prevBtn) this.#prevBtn.disabled = this.#realIndex === 0;
      if (this.#nextBtn) this.#nextBtn.disabled = this.#realIndex >= maxIndex;
    }
  }
  
  #announceSlide() {
    if (this.#liveRegion) {
      this.#liveRegion.textContent = this.#formatLabel(this.#labels.slideOf, {
        current: this.#realIndex + 1,
        total: this.#totalSlides
      });
    }
  }
  
  /* --- Navigation Logic --- */
  
  /**
   * Navigate to a specific slide index.
   * 
   * NOTE: #updateSlides() runs AFTER the transition completes.
   * This is intentional -- running it before causes visual artifacts
   * (inert/aria-hidden changes affect layout during animation).
   * Clone aria-hidden is managed in #updateSlides() to ensure
   * visible clones are interactive after navigation. (v4.3.6)
   */
  async #goToSlideInternal(newRealIndex, animate = true) {
    if (!this.#canNavigate()) return;
    
    /* --- Non-loop path --- */
    if (!this.#css('loop')) {
      const maxIndex = this.#getMaxIndex();
      newRealIndex = Math.max(0, Math.min(newRealIndex, maxIndex));
      if (newRealIndex === this.#realIndex) return;
      
      this.#realIndex = newRealIndex;
      this.#currentIndex = this.#realIndex;
      
      this.#updateProgressBar();
      this.#isTransitioning = true;
      this.#setTransform(this.#currentIndex, animate);
      
      if (animate && !this.#reducedMotion) {
        await this.#waitForTransition();
      }
      
      this.#isTransitioning = false;
      this.#updateSlides();
      this.#updateMask();
      this.#emitChange();
      return;
    }
    
    /* --- Loop path --- */
    this.#isTransitioning = true;
    
    const perMove = this.#getPerMove();
    
    if (newRealIndex < 0) {
      if (perMove > 1) {
        const pageStarts = this.#getPageStarts();
        const lastPageStart = pageStarts[pageStarts.length - 1] || 0;
        this.#currentIndex = this.#cloneCount - 1;
        this.#realIndex = lastPageStart;
      } else {
        this.#currentIndex = this.#cloneCount - 1;
        this.#realIndex = this.#totalSlides - 1;
      }
    } else if (newRealIndex >= this.#totalSlides) {
      this.#currentIndex = this.#cloneCount + this.#totalSlides;
      this.#realIndex = 0;
    } else {
      this.#currentIndex = newRealIndex + this.#cloneCount;
      this.#realIndex = newRealIndex;
    }
    
    this.#updateProgressBar();
    this.#setTransform(this.#currentIndex, animate);
    
    if (animate && !this.#reducedMotion) {
      await this.#waitForTransition();
    }
    
    // Instant jump after loop wrap
    if (this.#currentIndex < this.#cloneCount) {
      this.#currentIndex = this.#cloneCount + this.#realIndex;
      this.#setTransform(this.#currentIndex, false);
    } else if (this.#currentIndex >= this.#cloneCount + this.#totalSlides) {
      this.#currentIndex = this.#cloneCount + this.#realIndex;
      this.#setTransform(this.#currentIndex, false);
    }
    
    this.#isTransitioning = false;
    this.#updateSlides();
    this.#updateMask();
    this.#emitChange();
  }
  
  #goPrevInternal() {
    if (!this.#canNavigate()) return;
    const perMove = this.#getPerMove();
    if (perMove > 1) {
      const pageStarts = this.#getPageStarts();
      const currentPage = this.#getCurrentPage();
      if (currentPage > 0) {
        this.#goToSlideInternal(pageStarts[currentPage - 1]);
      }
    } else {
      this.#goToSlideInternal(this.#realIndex - 1);
    }
  }
  
  #goNextInternal() {
    if (!this.#canNavigate()) return;
    const perMove = this.#getPerMove();
    if (perMove > 1) {
      const pageStarts = this.#getPageStarts();
      const currentPage = this.#getCurrentPage();
      if (currentPage < pageStarts.length - 1) {
        this.#goToSlideInternal(pageStarts[currentPage + 1]);
      }
    } else {
      this.#goToSlideInternal(this.#realIndex + 1);
    }
  }
  
  #waitForTransition() {
    const duration = this.#getTransitionDuration();
    return new Promise((resolve) => {
      setTimeout(resolve, duration + 50);
    });
  }
  
  #emitChange() {
    this.emit(EVENTS.CHANGED, {
      index: this.#realIndex,
      total: this.#totalSlides,
      page: this.#getCurrentPage(),
      pageCount: this.#getPageCount()
    });
  }
  
  /* --- Autoplay --- */
  
  #startAutoplay() {
    if (!this.#css('autoplay') || this.#isAutoplayPaused || this.#isDisabled || this.#reducedMotion) return;
    this.#stopAutoplay();
    const interval = this.config.autoplayInterval;
    this.#autoplayTimer = setInterval(() => {
      if (!this.#isAutoplayPaused && !this.#isDragging && !this.#isDisabled && this.state.has(STATES.CAN_SCROLL)) {
        this.#goNextInternal();
      }
    }, interval);
  }
  
  #stopAutoplay() {
    if (this.#autoplayTimer) {
      clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = null;
    }
  }
  
  #pauseAutoplayInternal() {
    this.#isAutoplayPaused = true;
    this.#stopAutoplay();
    if (this.#pauseBtn) {
      this.#pauseBtn.setAttribute('aria-pressed', 'true');
      this.#pauseBtn.setAttribute('aria-label', this.#labels.play);
    }
  }
  
  #resumeAutoplayInternal() {
    this.#isAutoplayPaused = false;
    this.#startAutoplay();
    if (this.#pauseBtn) {
      this.#pauseBtn.setAttribute('aria-pressed', 'false');
      this.#pauseBtn.setAttribute('aria-label', this.#labels.pause);
    }
  }
  
  #toggleAutoplayInternal() {
    if (this.#isAutoplayPaused) this.#resumeAutoplayInternal();
    else this.#pauseAutoplayInternal();
  }
  
  /* --- Drag/Swipe --- */
  
  #commitDrag(e) {
    this.#isDragging = true;
    this.state.add(STATES.DRAGGING);
    this.#dragStep = this.#getSlideStep();
    
    // Cache progress bar layout values (avoid reflow during move)
    if (this.#progressBar) {
      const visibleCount = this.#getVisibleCount();
      const thumbWidth = Math.max(0.08, visibleCount / this.#totalSlides);
      const maxIndex = this.#getMaxIndex();
      this.#dragProgressCache = { thumbWidth, maxIndex };
    }
    
    this.#panel.style.transition = 'none';
    if (e.pointerId !== undefined) {
      this.setPointerCapture(e.pointerId);
    }
  }
  
  #handlePointerDown(e) {
    // Reset click suppression BEFORE canNavigate() guard (v4.3.4)
    this.#suppressNextClick = false;

    if (!this.#canNavigate()) return;
    if (e.button !== 0 && e.button !== undefined) return;

    const sliderDialog = this.closest('dialog');
    const hasOverlayingModal = [...document.querySelectorAll('dialog:modal')]
      .some(modal => modal !== sliderDialog && !modal.contains(this));
    if (hasOverlayingModal) return;
    
    this.#isOnInteractive = !!e.target.closest('button, a, [role="button"], input, select, textarea');
    this.#isTracking = true;
    
    const point = e.touches?.[0] || e;
    this.#dragStartX = point.clientX;
    this.#dragStartY = point.clientY;
    this.#dragStartTime = Date.now();
    this.#dragOffset = 0;
    
    if (!this.#isOnInteractive) {
      this.#commitDrag(e);
    }
  }
  
  #handlePointerMove(e) {
    if (!this.#isTracking) return;
    
    const point = e.touches?.[0] || e;
    const deltaX = point.clientX - this.#dragStartX;
    const deltaY = point.clientY - this.#dragStartY;
    
    if (!this.#isDragging && Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 10) {
      this.#isTracking = false;
      this.#isOnInteractive = false;
      return;
    }
    
    if (!this.#isDragging && this.#isOnInteractive) {
      if (Math.abs(deltaX) > 10) {
        this.#commitDrag(e);
      } else {
        return;
      }
    }
    
    if (!this.#isDragging) return;
    
    if (Math.abs(deltaX) > 10) {
      e.preventDefault();
    }
    
    this.#dragOffset = deltaX;
    setCSSVar(this.#panel, PROPERTIES.DRAG, `${this.#dragOffset}px`);
    
    // Live progress bar tracking during drag (pure math, no DOM reads)
    if (this.#dragProgressCache && this.#dragStep > 0) {
      const { thumbWidth, maxIndex } = this.#dragProgressCache;
      const effectiveIndex = this.#realIndex - (this.#dragOffset / this.#dragStep);
      const clamped = Math.max(0, Math.min(effectiveIndex, maxIndex));
      const progress = maxIndex === 0 ? 0 : clamped / maxIndex;
      const thumbLeft = progress * (1 - thumbWidth);
      setCSSVar(this.#progressBar, PROPERTIES.PROGRESS, thumbLeft);
    }
  }
  
  #handlePointerUp(e) {
    if (!this.#isTracking) return;
    
    const wasDragging = this.#isDragging;
    const wasOnInteractive = this.#isOnInteractive;
    
    this.#isTracking = false;
    this.#isDragging = false;
    this.#isOnInteractive = false;
    this.state.remove(STATES.DRAGGING);
    
    if (e.pointerId !== undefined) {
      try { this.releasePointerCapture(e.pointerId); } catch (_) { /* already released */ }
    }
    
    if (!wasDragging) {
      this.#dragOffset = 0;
      return;
    }
    
    this.#panel.style.transition = '';
    setCSSVar(this.#panel, PROPERTIES.DRAG, '0px');
    
    // Flag-based click suppression (v4.3.4)
    if (wasOnInteractive) {
      this.#suppressNextClick = true;
    }
    
    const deltaTime = Date.now() - this.#dragStartTime;
    const absOffset = Math.abs(this.#dragOffset);
    const itemWidth = this.#getItemWidth();
    const step = this.#getSlideStep();
    
    const swipeThreshold  = this.config.swipeThreshold;
    const swipeTimeout    = this.config.swipeTimeout;
    
    if (absOffset > swipeThreshold && deltaTime < swipeTimeout) {
      if (this.#dragOffset < 0) this.#goNextInternal();
      else this.#goPrevInternal();
    } else if (absOffset > itemWidth * 0.2) {
      const slidesToMove = Math.max(1, Math.round(absOffset / step));
      const targetIndex = this.#dragOffset < 0
        ? this.#realIndex + slidesToMove
        : this.#realIndex - slidesToMove;
      this.#goToSlideInternal(targetIndex);
    }
    
    this.#dragOffset = 0;
    this.#dragStep = 0;
    this.#dragProgressCache = null;
  }
  
  /* --- Keyboard --- */
  
  #handleKeydown(e) {
    if (!this.#canNavigate()) return;
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.#goPrevInternal();
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.#goNextInternal();
        break;
      case 'Home':
        e.preventDefault();
        this.#goToSlideInternal(0);
        break;
      case 'End':
        e.preventDefault();
        this.#goToSlideInternal(this.#getMaxIndex());
        break;
    }
  }
  
  /* --- Dynamic Listener Setup --- */
  
  #setupNavListeners() {
    if (this.#prevBtn) {
      this.#navCleanups.push(on(this.#prevBtn, 'click', () => this.#goPrevInternal()));
    }
    if (this.#nextBtn) {
      this.#navCleanups.push(on(this.#nextBtn, 'click', () => this.#goNextInternal()));
    }
  }
  
  #setupDotListeners() {
    const pageStarts = this.#getPageStarts();
    this.#dots.forEach((dot, i) => {
      const targetIndex = dot.dataset.startIndex !== undefined 
        ? parseInt(dot.dataset.startIndex, 10) 
        : pageStarts[i] ?? 0;
      this.#dotCleanups.push(on(dot, 'click', () => this.#goToSlideInternal(targetIndex)));
    });
  }
  
  #setupAutoplayListeners() {
    const pauseOnHover = this.config.pauseOnHover;
    const pauseOnFocus = this.config.pauseOnFocus;
    
    if (this.#pauseBtn && this.#css('autoplay') && !this.#reducedMotion) {
      this.#autoplayCleanups.push(on(this.#pauseBtn, 'click', () => this.#toggleAutoplayInternal()));
    }
    if (this.#css('autoplay') && pauseOnHover) {
      this.#autoplayCleanups.push(on(this, 'mouseenter', () => {
        if (!this.#isAutoplayPaused && !this.#isDisabled) this.#stopAutoplay();
      }));
      this.#autoplayCleanups.push(on(this, 'mouseleave', () => {
        if (!this.#isAutoplayPaused && !this.#isDisabled) this.#startAutoplay();
      }));
    }
    if (this.#css('autoplay') && pauseOnFocus) {
      this.#autoplayCleanups.push(on(this, 'focusin', () => {
        if (!this.#isAutoplayPaused && !this.#isDisabled) this.#stopAutoplay();
      }));
      this.#autoplayCleanups.push(on(this, 'focusout', (e) => {
        if (!this.contains(e.relatedTarget) && !this.#isAutoplayPaused && !this.#isDisabled) {
          this.#startAutoplay();
        }
      }));
    }
  }
  
  #cleanupNav() {
    this.#navCleanups.forEach(fn => fn());
    this.#navCleanups = [];
  }
  
  #cleanupDots() {
    this.#dotCleanups.forEach(fn => fn());
    this.#dotCleanups = [];
  }
  
  #cleanupAutoplay() {
    this.#autoplayCleanups.forEach(fn => fn());
    this.#autoplayCleanups = [];
  }
  
  /* --- Enable/Disable --- */
  
  #enableSlider(currentSettings) {
    this.#isDisabled = false;
    this.state.remove(STATES.DISABLED);
    this.#cssSettings = currentSettings.css;
    this.#navSettings = currentSettings.nav;
    
    // Phase 1: PRE-CLONE READ
    this.#invalidateLayout();
    this.#readLayout();
    
    // Phase 2: DOM MUTATIONS
    this.#createClones();
    if (!this.#css('loop')) {
      this.#allSlides = [...this.#originalSlides];
    }
    this.#createNavigation();
    this.#createDots();
    this.#createProgressBar();
    this.#setupNavListeners();
    this.#setupDotListeners();
    this.#setupAutoplayListeners();
    
    // Phase 3: POST-CLONE READ
    this.#readLayout();
    
    // Phase 4: WRITES (all from cache)
    this.#currentIndex = this.#css('loop') ? this.#realIndex + this.#cloneCount : this.#realIndex;
    this.#setTransform(this.#currentIndex, false);
    this.#showClones();
    this.#updateSlides();
    this.#updateMask();
    this.#updateScrollCapability();
    
    if (this.#css('autoplay') && !this.#reducedMotion) {
      this.#startAutoplay();
    }
    this.emit(EVENTS.ENABLED, { index: this.#realIndex });
  }
  
  #disableSlider() {
    this.#isDisabled = true;
    this.state.add(STATES.DISABLED);
    this.#invalidateLayout();
    this.state.remove(STATES.CAN_SCROLL);
    this.#stopAutoplay();
    this.#cleanupNav();
    this.#cleanupDots();
    this.#cleanupAutoplay();
    this.#removeClones();
    this.#removeNavigation();
    this.#removeDots();
    this.#removeProgressBar();
    
    setCSSVar(this.#panel, PROPERTIES.OFFSET, '0px');
    setCSSVar(this.#panel, PROPERTIES.DRAG, '0px');
    
    this.#originalSlides.forEach(slide => {
      slide.removeAttribute('aria-hidden');
      slide.removeAttribute('liftr-active');
      this.#setChildrenFocusable(slide, true);
    });
    this.emit(EVENTS.DISABLED, {});
  }
  
  /* --- Settings Change Handler --- */
  
  #handleChanges(changes) {
    if (this.#isDisabled) return;
    
    if (changes.initial) {
      this.#cssSettings = changes.css;
      this.#navSettings = changes.nav;
      if (this.#navSettings?.mode === 'auto') {
        NAV.applyOutput(this, this.#navSettings);
      }
      return;
    }
    
    let rebuildClones = false;
    let rebuildDots = false;
    
    if (changes.css) {
      const oldLoop = this.#css('loop');
      const oldDots = this.#css('dots');
      const oldAutoplay = this.#css('autoplay');
      const oldMode = this.#css('mode');
      
      this.#cssSettings = changes.css;
      
      const newLoop = this.#css('loop');
      const newDots = this.#css('dots');
      const newAutoplay = this.#css('autoplay');
      const newMode = this.#css('mode');
      
      if (newLoop !== oldLoop) rebuildClones = true;
      if (newMode !== oldMode) rebuildDots = true;
      
      if (newDots !== oldDots || rebuildDots) {
        this.#cleanupDots();
        this.#removeDots();
        this.#removeProgressBar();
        this.#dotsContainer = this.role(ROLES.NAV);
        this.#createDots();
        this.#createProgressBar();
        this.#setupDotListeners();
        this.#updateDotsUI();
        this.#updateProgressBar();
      }
      
      if (newAutoplay !== oldAutoplay) {
        this.#cleanupAutoplay();
        if (newAutoplay && !this.#reducedMotion) {
          this.#setupAutoplayListeners();
          this.#startAutoplay();
        } else {
          this.#stopAutoplay();
        }
      }
    }
    
    if (changes.nav) {
      const oldMode = this.#navSettings?.mode;
      const newMode = changes.nav?.mode;
      if (newMode !== oldMode) {
        this.#cleanupNav();
        this.#removeNavigation();
        this.#navSettings = changes.nav;
        this.#createNavigation();
        this.#setupNavListeners();
      } else {
        this.#navSettings = changes.nav;
        if (this.#navSettings?.mode === 'auto') {
          NAV.applyOutput(this, this.#navSettings);
        } else if (this.#navSettings?.mode === 'static') {
          if (this.#prevBtn) NAV.applyToButton(this.#prevBtn, this.#navSettings);
          if (this.#nextBtn) NAV.applyToButton(this.#nextBtn, this.#navSettings);
        }
      }
    }
    
    if (changes.size) {
      this.#invalidateLayout();
    }
    
    if (changes.size && !rebuildClones && this.#css('loop')) {
      const { containerWidth, itemWidth } = this.#readLayout();
      const newVisibleCount = Math.ceil(containerWidth / (itemWidth || 1));
      const newCloneCount = Math.min(newVisibleCount, this.#totalSlides);
      if (newCloneCount !== this.#cloneCount) rebuildClones = true;
    }
    
    if (changes.size && this.#css('mode') === 'all' && this.#generatedDotsContainer) {
      const currentDotCount = this.#dots.length;
      const newPageCount = this.#getPageCount();
      if (currentDotCount !== newPageCount) {
        this.#cleanupDots();
        this.#removeDots();
        this.#dotsContainer = this.role(ROLES.NAV);
        this.#createDots();
        this.#setupDotListeners();
        this.#updateDotsUI();
      }
    }
    
    if (rebuildClones) {
      this.#removeClones();
      this.#invalidateLayout();
      
      if (this.#css('loop')) {
        this.#readLayout();
        this.#createClones();
        this.#readLayout();
        this.#currentIndex = this.#realIndex + this.#cloneCount;
      } else {
        this.#allSlides = [...this.#originalSlides];
        this.#currentIndex = this.#realIndex;
        this.#readLayout();
      }
      
      this.#setTransform(this.#currentIndex, false);
      this.#showClones();
      this.#updateScrollCapability();
    }
  }
  
  /* --- Public API --- */
  
  goToSlide(index, animate = true) { this.#goToSlideInternal(index, animate); }
  prev() { this.#goPrevInternal(); }
  next() { this.#goNextInternal(); }
  pauseAutoplay() { this.#pauseAutoplayInternal(); }
  resumeAutoplay() { this.#resumeAutoplayInternal(); }
  toggleAutoplay() { this.#toggleAutoplayInternal(); }
  
  get currentIndex() { return this.#realIndex; }
  get totalSlides() { return this.#totalSlides; }
  get maxIndex() { return this.#getMaxIndex(); }
  get currentPage() { return this.#getCurrentPage(); }
  get pageCount() { return this.#getPageCount(); }
  get perMove() { return this.#getPerMove(); }
  get isDisabled() { return this.#isDisabled; }
  get isAutoplayPaused() { return this.#isAutoplayPaused; }
  get canScroll() { return this.state.has(STATES.CAN_SCROLL); }
}

/* =====================================================================
 * REGISTER & EXPORT
 * ===================================================================== */

customElements.define('liftr-slider', LiftrSlider);
export { LiftrSlider, ROLES, STATES, EVENTS, SETTINGS, PROPERTIES };